import { useAppSelector } from "../../../store/hooks";
import { useShoppingCart } from "../context/ShoppingCartContext";
import usePageLanguage from "../hooks/usePageLanguage";
import { CartItemRow } from "../ShoppingCart/CartItemRow";
import { formatDiscountLineAmount, pf } from "../utilities/helpers";
import CartExpiryCountdown from "../ShoppingCart/CartExpiryCountdown";
import { getComponentFromStructureMap } from "../utilities/componentIdentity";
import {
  buildCartDisplaySections,
  orderProductsToDisplayCartItems,
} from "../utilities/cartDisplay";
import {
  getConfirmedOrderGrandTotal,
  sumCartDiscount,
  sumCartProductsRegular,
  sumCartProductsTotal,
} from "../utilities/cartTotals";
import { showCheckoutSuccessHeader } from "./checkoutMode";
import { cartLineKey } from "../utilities/cartIdentity";
import type { CheckoutMode } from "./checkoutMode";

import React, { useEffect, useRef } from "react";

const languageTranslations = {
  et: {
    products: "TOOTED",
    total: "KOKKU",
    additionl_information: "Lisainfo",
    sum: "Summa",
    discount: "Kohandus",
    delivery: "Tarne",
    paymentComplete: "Tellimus esitatud!",
  },
  en: {
    products: "PRODUCTS",
    total: "TOTAL",
    sum: "Sum",
    additionl_information: "Additional information",
    discount: "Adjustment",
    delivery: "Delivery",
    paymentComplete: "Order submitted!",
  },
};

type CheckoutProductsPanelProps = {
  id: number | string;
  readOnly?: boolean;
  checkoutMode?: CheckoutMode;
};

export default function CheckoutProductsPanel({
  id,
  readOnly = false,
  checkoutMode = "edit",
}: CheckoutProductsPanelProps) {
  const {
    cartItems,
    cartItemsConfirmed,
    confirmedOrder,
    order,
    deliveryConfirmed,
    catalogIndex,
    pricingNow,
    totalCartDiscountConfirmed,
    totalCartSumConfirmed,
    additionalInfo,
    setAdditionalInfo,
  } = useShoppingCart();

  const displayItems = readOnly
    ? orderProductsToDisplayCartItems(cartItemsConfirmed)
    : cartItems;

  const deliveryForDisplay = readOnly
    ? deliveryConfirmed ?? order?.delivery ?? null
    : order?.delivery ?? null;
  const deliveryPrice =
    deliveryForDisplay && deliveryForDisplay.taxInclusivePriceAmount
      ? Number(deliveryForDisplay.taxInclusivePriceAmount)
      : 0;

  const cartLinesForTotals = displayItems.map((item) => ({
    productUuid: item.productUuid,
    quantity: item.quantity,
    invalid: item.invalid,
  }));
  const productsTotal = sumCartProductsTotal(
    cartLinesForTotals,
    catalogIndex,
    pricingNow
  );
  const productsRegularSum = sumCartProductsRegular(
    cartLinesForTotals,
    catalogIndex,
    pricingNow
  );
  const computedProductsDiscount = sumCartDiscount(
    cartLinesForTotals,
    catalogIndex,
    pricingNow
  );
  const productsDiscount = readOnly
    ? totalCartDiscountConfirmed > 0
      ? totalCartDiscountConfirmed
      : computedProductsDiscount
    : computedProductsDiscount;

  const computedReadOnlyTotal =
    productsTotal + (deliveryForDisplay?.uuid ? deliveryPrice : 0);
  const confirmedOrderTotal = getConfirmedOrderGrandTotal(confirmedOrder);
  const hasContextConfirmedTotal =
    Number.isFinite(totalCartSumConfirmed) &&
    (totalCartSumConfirmed !== 0 || computedReadOnlyTotal === 0);

  const displayTotal = readOnly
    ? confirmedOrderTotal ??
      (hasContextConfirmedTotal ? totalCartSumConfirmed : computedReadOnlyTotal)
    : productsTotal + deliveryPrice;

  const showSuccessHeader = showCheckoutSuccessHeader(
    checkoutMode,
    confirmedOrder
  );

  const useAutosizeTextArea = (
    textAreaRef: HTMLTextAreaElement | null,
    value: string
  ) => {
    useEffect(() => {
      if (textAreaRef) {
        textAreaRef.style.height = "0px";
        const scrollHeight = textAreaRef.scrollHeight;

        textAreaRef.style.height = scrollHeight + "px";
      }
    }, [textAreaRef, value]);
  };

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutosizeTextArea(textAreaRef.current, additionalInfo);

  const component = (useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, id)
  ) as any) ?? ({
    id: typeof id === "number" ? id : -1,
    parent_id: null,
    childElements: [],
    childWebpageComponentRelations: [],
    sort_order: 0,
    type: "Checkout",
    uuid: null,
    version: 1,
    webpage_id: 0,
    attributes: {
      additional_info: "hidden",
      styles: {},
      classes: "",
      content_width_size: 0,
      content_width_unit: "px",
      typography_id: 0,
    },
  } as any);

  const locale = usePageLanguage();
  const cartDisplaySections = buildCartDisplaySections(displayItems, locale);

  const showSumRow = readOnly
    ? !!(deliveryForDisplay?.uuid || productsDiscount !== 0)
    : !!(order?.delivery?.uuid || productsDiscount !== 0);

  return (
    <div className="panel" id="checkoutProductsPanel">
      <div
        className="panel-heading"
        style={{
          display: showSuccessHeader ? undefined : "flex",
          justifyContent: showSuccessHeader ? undefined : "space-between",
          alignItems: showSuccessHeader ? undefined : "center",
        }}
      >
        {showSuccessHeader && (
          <div className="checkoutProductsPanelConfirmedHeading">
            <span>{languageTranslations[locale]?.paymentComplete}</span>
            <span>
              <svg
                style={{
                  display: "block",
                }}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                width="24px"
                height="24px"
              >
                <path d="M0 0h24v24H0z" fill="none" />
                <path d="M9 16.2l-3.5-3.5L4 14l5 5 12-12-1.4-1.4z" />
              </svg>
            </span>
          </div>
        )}
        <div className="panel-title">
          <span className="badge badge-square badge-primary ">1</span>
          <span className="panel-title-text">
            {languageTranslations[locale]?.products}
          </span>
        </div>
        {!readOnly && (
          <div className="products-expire-container">
            <CartExpiryCountdown />
          </div>
        )}
      </div>
      <div className="panel-body">
        <div className="row">
          <div className="col-xs-12 padding-top-20">
            <div
              className="table"
              style={{ fontSize: "14px", width: "100%", paddingTop: "5px" }}
            >
              <div
                className="cart-line-items-live"
              >
                {cartDisplaySections.map((section) => (
                  <React.Fragment key={section.key}>
                    {(section.label || section.showGroupRow) && (
                      <div className="cartEventGroupRow">
                        {section.label}
                      </div>
                    )}
                    {section.items.map((item) => (
                      <CartItemRow
                        key={cartLineKey(item)}
                        {...item}
                        readOnly={readOnly}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
          {!readOnly &&
            component.attributes.additional_info === "visible" && (
              <div>
                <div
                  className="col-xs-12"
                  style={{ marginBottom: "25px", marginTop: "20px" }}
                >
                  <textarea
                    id="note_textarea"
                    className="form-control"
                    ref={textAreaRef}
                    style={{
                      fontWeight: "400",
                      lineHeight: "1.5",
                      padding: "0px 0px",
                      margin: "0px",
                      fontSize: "15px",
                      width: "100%",
                      color: "black",
                      borderWidth: "0px 0px 1px 0px",
                      borderRadius: "0px",
                      borderColor: "gray",
                      outline: "none",
                      boxShadow: "none",
                    }}
                    value={additionalInfo}
                    onChange={(event) => {
                      setAdditionalInfo(event.target.value);
                    }}
                    placeholder={
                      languageTranslations[locale]?.additionl_information
                    }
                  ></textarea>
                </div>
              </div>
            )}

          {showSumRow && (
            <div className="col-xs-12 padding-top-20">
              <div style={{ fontWeight: "400", fontSize: "15px" }}>
                {languageTranslations[locale]?.sum}
                <span className="pull-right">
                  <span className="cartTotal">
                    {pf(
                      readOnly
                        ? productsRegularSum
                        : productsRegularSum
                    )}
                  </span>
                  &euro;
                </span>
              </div>
            </div>
          )}

          {productsDiscount !== 0 ? (
            <div className="col-xs-12 ">
              <div
                className="discount-price-color"
                style={{ fontWeight: "400", fontSize: "15px" }}
              >
                {languageTranslations[locale]?.discount}
                <span className="pull-right">
                  <span className="cartTotal">
                    {formatDiscountLineAmount(productsDiscount)}
                  </span>{" "}
                  &euro;
                </span>
              </div>
            </div>
          ) : (
            ""
          )}
          {(readOnly ? deliveryForDisplay?.uuid : order?.delivery?.uuid) && (
            <div className="col-xs-12 ">
              <div style={{ fontWeight: "400", fontSize: "15px" }}>
                {languageTranslations[locale]?.delivery}
                <span className="pull-right">
                  <span className="cartTotal">
                    {pf(deliveryPrice)}
                  </span>{" "}
                  &euro;
                </span>
              </div>
            </div>
          )}
          <div className="col-xs-12 ">
            <div style={{ fontWeight: "400", fontSize: "18px" }}>
              {languageTranslations[locale]?.total}
              <span className="pull-right">
                <span className="cartTotal">{pf(displayTotal)}</span>
                &euro;
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
